import { useFormik } from "formik"
import React, { useState } from "react"
import {
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner
} from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import * as Yup from "yup"
import CustomSelect from "../../../components/CustomSelect"
import ModalComponent from "../../../components/Modal"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import { handleOnBlur, handleOnchange } from "../../../lib/GlobalMethods"
import { connectorLevel, currentType } from "../../../lib/GlobalVariables"
import { _numberRegex_ } from "../../../lib/Regex"

const AddEditConnector = ({
  isUpdate,
  setIsUpdate,
  show,
  setShow,
  fetchData,
  handleClose,
}) => {
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const validationSchema = Yup.object({
    connector_type: Yup.string()
      .min(2, "Enter at least 2 characters for Connector Name")
      .required("Connector Name is required"),
    max_power_output: Yup.string().required("Maximum Power Output is required"),
    max_current_output: Yup.string().required(
      "Maximum Current Output is required"
    ),

    level: Yup.object().required("Level is required"),
    current_type: Yup.object().required("Current Type Name is required"),
    connector_img: Yup.mixed()
      .test("required", "Connector image is required", (file) => {
        if (isUpdate) {
          return file !== null && file !== undefined
        }

        return file
      })
      .test(
        "fileType",
        "File format is not supported, Please use .jpg/.png/.jpeg",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return (
            file && ["image/png", "image/jpg", "image/jpeg"].includes(file.type)
          )
        }
      )
      .test(
        "fileSize",
        "File size too large, max file size is 500 Kb",
        (file) => {
          if (isUpdate && (!file || typeof file === "string")) return true
          return file && file.size <= 512000
        }
      ),
  })

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      values = {
        ...values,
        level: values.level.value,
        current_type: values.current_type.value,
        connector_img:
          typeof values?.connector_img === "object"
            ? values?.connector_img
            : isUpdate.connector_img,
      }

      const data = new FormData()
      for (const key in values) {
        data.append(key, values[key])
      }
      const response = await axiosProvider({
        method: isUpdate ? "PATCH" : "POST",
        endpoint: isUpdate
          ? admin.updateConnector + isUpdate.id
          : admin.createConnector,
        data: data,
      })

      if (response?.data?.code === 201 || response?.data?.code === 200) {
        toast.success(response.data.message)
        setLoading(false)
        setShow(false)
        setIsUpdate(false)
        fetchData()
        resetForm()
      } else {
        setLoading(false)
        throw { message: response.data.message }
      }
    } catch (error) {
      setLoading(false)
      toast.error(error.message)
    }
  }

  const formik = useFormik({
    initialValues: isUpdate
      ? {
          connector_type: isUpdate.connector_type,
          max_power_output: isUpdate.max_power_output,
          max_current_output: isUpdate.max_current_output,
          level: { value: isUpdate?.level, label: `Level ${isUpdate?.level} ` },
          current_type: {
            value: isUpdate?.current_type,
            label: isUpdate?.current_type,
          },
          connector_img: isUpdate?.connector_img?.split("/").pop(),
        }
      : {
          connector_type: "",
          max_power_output: "",
          max_current_output: "",
          level: "",
          current_type: "",
          connector_img: "",
        },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, setFieldValue, resetForm, initialValues } =
    formik

  return (
    <>
      <ModalComponent
        show={show}
        modalsize={"lg"}
        backdrop="static"
        animationFade={true}
        onHide={() => {
          handleClose()
          resetForm()
        }}
        loading={loading}
        modeltitle={isUpdate ? "Update Connectors" : "Add Connectors"}
        submitname={
          loading ? (
            <Spinner size="sm" animation="border" variant="white" />
          ) : isUpdate ? (
            "Update"
          ) : (
            "Add"
          )
        }
        btnclosetext={"Cancel"}
        handleSubmit={formik.handleSubmit}
        children={
          <Form noValidate>
            <Row xs={"auto"}>
              <Col xs={12}>
                <FloatingLabel
                  controlId="connectorname"
                  label={
                    <>
                      Connector Name <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="connector_type"
                    autoComplete="false"
                    maxLength={20}
                    value={values.connector_type}
                    onChange={(e) => handleOnchange(e, false, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={
                      touched.connector_type && !!errors.connector_type
                    }
                    isValid={touched.connector_type && !errors.connector_type}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.connector_type}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="maxpoweroutput"
                  label={
                    <>
                      Max Output Power
                      <small style={{ fontSize: "0.75rem" }}>(kw)</small>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="max_power_output"
                    autoComplete="false"
                    maxLength={10}
                    value={values.max_power_output}
                    onChange={(e) => handleOnchange(e, _numberRegex_, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={
                      touched.max_power_output && errors.max_power_output
                    }
                    isValid={
                      touched.max_power_output && !errors.max_power_output
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.max_power_output}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="maxcurrentoutput"
                  label={
                    <>
                      Max Current Output
                      <small style={{ fontSize: "0.75rem" }}>(Amps)</small>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder=""
                    name="max_current_output"
                    autoComplete="false"
                    maxLength={10}
                    value={values.max_current_output}
                    onChange={(e) => handleOnchange(e, _numberRegex_, formik)}
                    onBlur={(e) => handleOnBlur(e, formik)}
                    isInvalid={
                      touched.max_current_output && errors.max_current_output
                    }
                    isValid={
                      touched.max_current_output && !errors.max_current_output
                    }
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.max_current_output}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                <label>
                  Connector level<span className="required_star">*</span>
                </label>

                <CustomSelect
                  value={values?.level}
                  handleSelectChange={(selectedOption) => {
                    setFieldValue("level", selectedOption)
                  }}
                  options={connectorLevel}
                  isMulti={false}
                  placeholder={"Select Level"}
                  isInvalid={touched?.level && errors?.level}
                  isValid={touched?.level && !errors?.level}
                />

                {touched?.level && errors?.level && (
                  <p className="invalid_error">{errors?.level}</p>
                )}
              </Col>
              <Col xs={12} className="mb-3 custom_selectinvalid_bdr">
                <label>
                  Current Type<span className="required_star">*</span>
                </label>

                <CustomSelect
                  value={values?.current_type}
                  handleSelectChange={(selectedOption) => {
                    setFieldValue("current_type", selectedOption)
                  }}
                  options={currentType}
                  isMulti={false}
                  placeholder={"Select Current Type"}
                />

                {touched?.current_type && errors?.current_type && (
                  <p className="invalid_error">{errors?.current_type}</p>
                )}
              </Col>
              <Col xs={12}>
                <FloatingLabel
                  controlId="connectorimg"
                  label={
                    <>
                      Connector Image <span className="required_star">*</span>
                    </>
                  }
                >
                  {values?.connector_img ? (
                    <div
                      className={`previewnm_ip ${
                        touched?.connector_img && errors?.connector_img
                          ? "is-invalid"
                          : ""
                      }`}
                    >
                      <TooltipComponent
                        toolplace="top"
                        tooltipText={
                          typeof values?.connector_img === "object"
                            ? values?.connector_img.name
                            : values?.connector_img
                        }
                      >
                        <p className="mb-0 previewnm_title">
                          {typeof values?.connector_img === "object"
                            ? values?.connector_img.name
                            : values?.connector_img}
                        </p>
                      </TooltipComponent>
                      <span
                        className="remove_previewnm"
                        onClick={() => {
                          setFieldValue("connector_img", "")
                        }}
                      >
                        <i className="icon_main cross_icon"></i>
                      </span>
                    </div>
                  ) : (
                    <Form.Control
                      type="file"
                      placeholder=""
                      autoComplete="false"
                      name="connector_img"
                      onChange={async (e) => {
                        setFieldValue("connector_img", e?.target?.files[0])
                      }}
                      isInvalid={
                        touched?.connector_img && errors?.connector_img
                      }
                      isValid={touched?.connector_img && !errors?.connector_img}
                    />
                  )}

                  <Form.Control.Feedback type="invalid">
                    {errors.connector_img}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>
          </Form>
        }
      />
    </>
  )
}

export default AddEditConnector
